import axios, { type AxiosError, type InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { getAccessToken, getRefreshToken, redirectToAuth, removeTokens } from '../auth';
import { getLocaleFromCookie } from '../../components/lang/LocalizationProvider';
import { logApiError } from '../../services/errorReporting';
import { errorParams } from '../consts/errorParams';
import { paramsKeys } from '../consts/params';
import { randomUUID } from '../uuid';
import env from '../env';
import { httpHeaders } from '../consts/httpHeaders';
// eslint-disable-next-line import/no-cycle -- using client within client callback works as exptected
import { sendTokenRefreshRequest, REFRESH_TOKEN_ROUTE } from './accountApi';

const appIdentity = 'my.clearvpn.com';

interface DefaulClearAPIError {
  code: number;
  message: string;
}

const clearApiClient = axios.create({
  baseURL: env.API_BASE_URL,
  headers: {
    [httpHeaders.APP_IDENTITY]: appIdentity,
    'Content-Type': 'application/json',
    'User-Locale': getLocaleFromCookie(),
  },
  withCredentials: false,
  meta: { shouldDropToLogin: true },
});

const unauthenticatedHTTPCode = 401;
const unauthenticatedResponseCode = 16;

const injectHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  config.headers.Authorization = getAccessToken();
  config.headers[httpHeaders.TRACE_ID] = randomUUID();

  return config;
};

let refreshPromise: Promise<void> | null = null;

clearApiClient.interceptors.request.use((config) => {
  const configWithHeaders = injectHeaders(config);

  if (refreshPromise && config.url !== REFRESH_TOKEN_ROUTE) {
    return refreshPromise.then(() => configWithHeaders);
  }

  return configWithHeaders;
});

const dropToLoginIfNeeded = (error: AxiosError) => {
  if (error.config?.meta.shouldDropToLogin) {
    redirectToAuth();
  }
};

clearApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAxiosError<DefaulClearAPIError>(error)) {
      if (
        error.response?.status === unauthenticatedHTTPCode &&
        error.response.data.code === unauthenticatedResponseCode &&
        error.config?.url !== REFRESH_TOKEN_ROUTE
      ) {
        const refreshToken = getRefreshToken();

        if (refreshToken) {
          if (!refreshPromise) {
            refreshPromise = sendTokenRefreshRequest(refreshToken);
            refreshPromise.then(
              () => {
                refreshPromise = null;
              },
              (refreshError: unknown) => {
                // refreash failed, redirect to the pit
                removeTokens();
                if (error.response?.data.message === 'delete account') {
                  window.location.href = `/enter?${paramsKeys.ERROR}=${errorParams.accountTerminated}`;
                }
                dropToLoginIfNeeded(error);
                throw refreshError;
              },
            );
          }

          return refreshPromise.then(() => {
            if (error.config) {
              return clearApiClient(injectHeaders(error.config));
            }
          });
        }
        // no refresh token, but go unauthenticated, redirect to the pit
        if (error.config?.meta.shouldDropToLogin) {
          redirectToAuth();
        }
      }

      logApiError(error);
      throw error;
    } else {
      removeTokens();
      dropToLoginIfNeeded(error as AxiosError);

      logApiError(error as AxiosError);
      throw error;
    }
  },
);

const isApiError = (error: unknown): error is AxiosError<DefaulClearAPIError> =>
  isAxiosError<DefaulClearAPIError>(error);

export { clearApiClient, isApiError };
